<template>
  <available-section section="fdoc-integrations">
    <main class="integration">
      <div class="integration__container">
        <button-back />
        <h1 class="integration__title">Настройка интеграции с сервисом F.doc</h1>
        <ol class="integration__number-list integration__subtitle">
          <li>
            Зарегистрируйтесь в сервисе F.doc по
            <a
              href="https://fdoc.ru/referral/?utm_source=partner&utm_medium=referral&utm_campaign=1232&utm_content=32top"
              class="integration__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              ссылке
            </a>.
          </li>
          <li>
            Настройте личный кабинет и добавьте сотрудников, которые имеют
            право подписывать документы ПЭП (простой электронной подписью).
          </li>
          <li v-if="isIntegrated && !isLinked">
            Введите логин и пароль от учетной записи в F.doc.
          </li>
        </ol>
        <div v-if="!isAwaitRequest" class="isAwaitRequest">
          <div v-if="!isIntegrated && !isLinked" class="integration__api-key-section">
            <input-text
              title="Введите ApiKey, полученный в ЛК F.doc"
              required
              :max-length="20"
              v-model="apiKey"
              :violations="apiKeyInputErrors"
            />
            <button
              class="integration__button integration__button--save"
              v-on:click="saveApiKey"
              type="button"
            >
              Сохранить
            </button>
          </div>
          <div v-else-if="isIntegrated && !isLinked" class="integration__credentials-section">
            <input-text
              title="Логин"
              required
              :max-length="50"
              v-model="login"
              :violations="loginInputErrors"
            />
            <input-text
              title="Пароль"
              required
              :max-length="20"
              v-model="password"
              :violations="passwordInputErrors"
            />
            <button
              class="integration__button integration__button--send"
              v-on:click="send"
              type="button"
            >
              Отправить
            </button>
            <h2 v-if="employeesTableShow"><br>Сопоставьте сотрудников в МИС и F.doc<br></h2>
            <matching-external-employees
              v-if="employeesTableShow"
              external-employees-column-head-title="ФИО сотрудника в F.Doc"
              v-bind:employees="employees"
              v-bind:external-employees="externalEmployees"
              v-on:link-employee="onLinkEmployee"
            />
          </div>
          <div v-else-if="isIntegrated && isLinked" class="integration__connected-section">
            <h2 v-if="employeesTableShow"><br>Сопоставьте сотрудников в МИС и F.doc<br></h2>
            <matching-external-employees
              v-if="employeesTableShow"
              external-employees-column-head-title="ФИО сотрудника в F.Doc"
              v-bind:employees="employees"
              v-bind:external-employees="externalEmployees"
              v-on:link-employee="onLinkEmployee"
            />
          </div>
        </div>
        <support-contacts class="integration__contacts" title="Если у вас возникли вопросы, свяжитесь с нами:" />
      </div>
    </main>
  </available-section>
</template>

<script>
import ButtonBack from '@/components/ButtonBack.vue';
import SupportContacts from '@/components/SupportContacts.vue';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import notifierMixin from '@/mixins/notifierMixin';
import InputText from '@/components/InputText.vue';
import MatchingExternalEmployees from '@/components/MatchingExternalEmployees.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

export default {
  name: 'ClinicPanelIntegrationFDoc',
  mixins: [
    notifierMixin,
  ],
  components: {
    MatchingExternalEmployees,
    InputText,
    AvailableSection,
    SupportContacts,
    ButtonBack,
  },
  created() {
    this.fetchPage();
  },
  computed: {
    loginInputErrors: resolveInputErrors('login'),
    passwordInputErrors: resolveInputErrors('password'),
    apiKeyInputErrors: resolveInputErrors('apiKey'),
  },
  methods: {
    /**
     * @description Логика кнопки "Сохранить"
     * @returns {string}
     */
    saveApiKey() {
      const params = {
        apiKey: this.apiKey,
      };
      const onSuccess = async (response) => {
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.fetchPage();
      };
      const onFail = (response) => {
        this.notifyFail(response.getTitle());
        this.inputErrors = response.allCauses();
      };
      this
        .$fDocIntegrationGateway
        .createIntegration(params)
        .then((result) => {
          result.fold(onFail, onSuccess);
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Логика кнопки "Отправить"
     * @returns {string}
     */
    send() {
      this.isAwaitRequest = true;
      const params = {
        login: this.login,
        password: this.password,
      };
      const onSuccess = async (response) => {
        this.employees = response.misEmployees;
        this.externalEmployees = response.fdocEmployees;
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.employeesTableShow = true;
      };
      const onFail = (response) => {
        this.notifyFail(response.getTitle());
        this.inputErrors = response.allCauses();
        this.employeesTableShow = false;
      };
      this
        .$fDocIntegrationGateway
        .getFDocMisEmployeeListByPassword(params)
        .then((result) => {
          result.fold(onFail, onSuccess);
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Обработать связывание сотрудника МИС c FDoc.
     * @param {Employee} employee   Сотрудник.
     * @param {?number}  externalId Идентификатор сотрудника FDoc.
     * @returns {void}
     */
    onLinkEmployee(employee, externalId) {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.employees = this.employees.map(
          (candidate) => (candidate === employee ? employee.setExternalId(externalId) : candidate),
        );
      };
      const onFail = (error) => this.notifyFail(error.getTitle());
      this
        .$fDocIntegrationGateway
        .linkEmployeeFdoc(employee.getId(), externalId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получение флагов (isIntegrated, isLinked)
     */
    fetchPage() {
      this.isAwaitRequest = true;
      this
        .$fDocIntegrationGateway
        .getPage()
        .then((result) => {
          this.isIntegrated = result.isIntegrated;
          this.isLinked = result.isLinked;
        })
        .finally(() => {
          if (this.isIntegrated && this.isLinked) {
            this.getEmployeesByUserId();
          }
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Получение сотрудников MIS, FDoc, с использованием
     * id текущего слинкованного сотрудника
     */
    getEmployeesByUserId() {
      this.isAwaitRequest = true;
      const params = {
        login: this.login,
        password: this.password,
      };
      const onSuccess = async (response) => {
        this.employees = response.misEmployees;
        this.externalEmployees = response.fdocEmployees;
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.employeesTableShow = true;
      };
      const onFail = (response) => {
        this.notifyFail(response.getTitle());
        this.inputErrors = response.allCauses();
        this.employeesTableShow = false;
      };
      this
        .$fDocIntegrationGateway
        .getFDocMisEmployeeListByExternalId(params)
        .then((result) => {
          result.fold(onFail, onSuccess);
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
  },
  data() {
    return {
      /**
       * @description Логин сотрудника.
       * @type {string}
       */
      login: '',
      /**
       * @description ApiKey интеграции F.Doc.
       * @type {string}
       */
      apiKey: '',
      /**
       * @description Пароль сотрудника.
       * @type {string}
       */
      password: '',
      /**
       * @description Залинкован ли текущий пользователь с фдок?
       * @type {boolean} - будет True, если по id текущего пользователя можно будет найти строку
       * в fdoc_employees
       */
      isLinked: false,
      /**
       * @description Сотрудники клиники
       * @type {Employee[]}
       */
      employees: [],
      /**
       * @description Нарушения ограничений.
       * @type {any}
       */
      inputErrors: {},
      /**
       * @description Имеется ли интеграция у клиники с FDoc?
       * @type {boolean} - будет True, если для текущей клиники есть запись в fdoc_integrations
       * при условии, что столбец disabled_at в записи будет равен null.
       */
      isIntegrated: false,
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Сотрудники fdoc
       */
      externalEmployees: [],
      /**
       * @description Флаг, показывающий нужно ли показывать таблицу линковки
       */
      employeesTableShow: false,
    };
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.buttons {
  display: flex;
  flex-direction: row;
  gap: 10px;
}
.integration {
  width: 100%;
  height: 100%;
  overflow: scroll;

  &__container {
    padding: 38px 20px 200px 23px;
  }

  &__title {
    font-weight: 600;
    font-size: 38px;
    line-height: 46px;
    color: #000000;
    margin: 0 0 22px 0;
  }

  &__subtitle {
    display: block !important;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
  }

  ol:is(&__number-list) {
    margin-bottom: 10px;
    padding-left: 18px;
  }
  &__api-key-section,
  &__credentials-section {
    padding-top: 18px;
  }

  &__connection {
    background-color: var(--section-background-color);
    padding: 40px 50px 35px 40px;
    margin: 46px 0 0 0;
    &-buttons {
      display: flex;
      gap: 10px;
      margin: 40px 0 0 0;
    }
  }

  &__button {
    @include buttons.btn-blue(244px, 48px, 16px);
    max-width: 100%;

    &:disabled {
      background-color: var(--unfocus-color);
    }
  }

  &__contacts {
    margin: 46px 0 0 0;
  }
}

@media screen and (max-width: 1240px) {
  .integration {
    &__container {
      width: 100%;
      padding: 45px 20px 145px;
    }
  }
}

@media screen and (max-width: 640px) {
  .integration {
    &__container {
      padding: 45px 10px 145px;
    }
  }
}

@media screen and (max-width: 480px) {
  .integration {
    &__connection {
      background-color: transparent;
      padding: 0 0 20px 0;
    }
  }
}
</style>
